import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';

export interface ListPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Optional noun for the summary, e.g. "patients" → "of 42 patients". */
  itemLabel?: string;
  className?: string;
  /** Max numbered page buttons to show (windowed around the current page). */
  maxButtons?: number;
}

/**
 * Server-driven list pagination footer: "Showing X–Y of Z" + prev/next + page numbers.
 * Hidden when there is nothing to show (`total === 0`). Always renders controls when
 * there is at least one page of results so the range summary stays visible.
 */
export function ListPagination({
  page,
  pageSize,
  total,
  onPageChange,
  itemLabel,
  className,
  maxButtons = 5,
}: ListPaginationProps): JSX.Element | null {
  if (total <= 0 || pageSize <= 0) return null;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);
  const showControls = totalPages > 1;

  const windowStart = Math.max(1, Math.min(safePage - Math.floor(maxButtons / 2), totalPages - maxButtons + 1));
  const pages = Array.from({ length: Math.min(totalPages, maxButtons) }, (_, i) => windowStart + i).filter(
    (p) => p <= totalPages,
  );

  return (
    <div
      className={cn(
        'flex items-center justify-between border-t border-[var(--border-subtle)] px-5 py-4',
        className,
      )}
    >
      <div className="text-[12px] text-ink-500">
        Showing{' '}
        <span className="font-semibold tabular-nums text-ink-900">
          {from}–{to}
        </span>{' '}
        of{' '}
        <span className="font-semibold tabular-nums text-ink-900">{total}</span>
        {itemLabel ? ` ${itemLabel}` : null}
      </div>
      {showControls ? (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Previous page"
            onClick={() => onPageChange(Math.max(1, safePage - 1))}
            disabled={safePage <= 1}
            className="h-8 w-8 text-ink-500"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {pages.map((p) => (
            <Button
              key={p}
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Page ${p}`}
              aria-current={p === safePage ? 'page' : undefined}
              onClick={() => onPageChange(p)}
              className={cn(
                'h-8 w-8 rounded-md border-transparent text-[12px] font-semibold shadow-none',
                p === safePage
                  ? 'bg-ink-900 text-white hover:bg-ink-800'
                  : 'text-ink-700 hover:bg-surface-100',
              )}
            >
              {p}
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Next page"
            onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
            disabled={safePage >= totalPages}
            className="h-8 w-8 text-ink-500"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
